import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MoreHorizontal, Pencil, Plus, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  customersApi,
  type BillingPreview,
  type CustomerPlan,
  type CustomerPlanInput,
} from "@/features/customers/api";
import { planEconomics } from "@/features/customers/economics";
import { money, perMinute } from "@/features/customers/format";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { cn } from "@/lib/utils";

const KEY = ["customer-plans"] as const;

function errText(e: unknown): string {
  const detail = (e as { response?: { data?: { detail?: string; name?: string[] } } })
    ?.response?.data;
  return detail?.detail || detail?.name?.[0] || "Something went wrong.";
}

export default function CustomerPlansPage() {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const [editing, setEditing] = useState<CustomerPlan | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleteFor, setDeleteFor] = useState<CustomerPlan | null>(null);

  const plansQ = useQuery({ queryKey: KEY, queryFn: customersApi.plans });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: KEY });
    // The Add-customer dialog reads the same plans.
    qc.invalidateQueries({ queryKey: ["customers"] });
  };

  const remove = useMutation({
    mutationFn: (id: string) => customersApi.removePlan(id),
    onSuccess: () => {
      toast.success("Plan deleted.");
      invalidate();
      setDeleteFor(null);
    },
    onError: (e) => toast.error(errText(e)),
  });

  if (!hasMinRole(user?.role, "admin")) return <Navigate to="/" replace />;

  const plans = plansQ.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Customer plans</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            What you sell to your own customers: a bundle of minutes for a
            monthly price, both of them yours to set.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" />
          New plan
        </Button>
      </div>

      {plansQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
          <Skeleton className="h-44" />
        </div>
      ) : plans.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
            <p className="text-sm text-muted-foreground">
              No plans yet. Every customer must be on one, so create your first
              before adding customers.
            </p>
            <Button onClick={() => setCreating(true)}>
              <Plus className="size-4" />
              Create a plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={() => setEditing(plan)}
              onDelete={() => setDeleteFor(plan)}
            />
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        These prices are what your customers pay you. What you pay for each
        customer is on the{" "}
        <Link to="/billing" className="underline underline-offset-2">
          Billing page
        </Link>
        .
      </p>

      {(creating || editing) && (
        <PlanDialog
          plan={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={invalidate}
        />
      )}

      <AlertDialog open={!!deleteFor} onOpenChange={(v) => !v && setDeleteFor(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteFor?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteFor?.customer_count
                ? `${deleteFor.customer_count} customer(s) are on this plan, so it can't be deleted. Deactivate it instead to stop offering it to new customers.`
                : "This plan will be gone. Customers already on other plans are unaffected."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!!deleteFor?.customer_count || remove.isPending}
              onClick={() => deleteFor && remove.mutate(deleteFor.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PlanCard({
  plan,
  onEdit,
  onDelete,
}: {
  plan: CustomerPlan;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className={plan.is_active ? undefined : "opacity-60"}>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate font-medium">{plan.name}</p>
            {!plan.is_active && (
              <span className="text-xs text-muted-foreground">Inactive</span>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="size-8 shrink-0">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <p className="text-2xl font-semibold">
          {money(plan.price_cents)}
          <span className="text-sm font-normal text-muted-foreground"> / month</span>
        </p>

        <div className="space-y-1 text-sm text-muted-foreground">
          <p>{plan.included_minutes.toLocaleString()} minutes included</p>
          <p>{perMinute(plan.rate_per_minute_cents)} per extra minute</p>
        </div>

        {plan.description && (
          <p className="text-xs text-muted-foreground">{plan.description}</p>
        )}

        <p className="flex items-center gap-1.5 border-t pt-3 text-xs text-muted-foreground">
          <Users className="size-3.5" />
          {plan.customer_count} customer{plan.customer_count === 1 ? "" : "s"}
        </p>
      </CardContent>
    </Card>
  );
}

/** Live margin while the plan is being typed: their price, Chawowa's cut, yours.
 *
 * Shown before the plan is saved rather than after the first invoice, because
 * a plan whose included minutes cost more than its price is not obvious from
 * the two numbers alone.
 */
function MarginPanel({
  priceCents,
  minutes,
  derived,
  preview,
  loading,
}: {
  priceCents: number;
  minutes: number;
  derived: number;
  preview?: BillingPreview;
  loading: boolean;
}) {
  const e = preview
    ? planEconomics({
        priceCents,
        includedMinutes: minutes,
        feeCents: preview.fee_cents,
        costPerMinuteCents: preview.per_minute_cents,
      })
    : null;
  const profitable = !e || e.profit >= 0;

  return (
    <div className="space-y-2 rounded-lg border bg-muted/40 p-3 text-sm">
      <Row label="Your customer pays you" value={`${money(priceCents)} /mo`} strong />

      {loading && <Skeleton className="h-14 w-full" />}

      {e && (
        <>
          <div className="space-y-1 border-t pt-2">
            <Row label="Chawowa charges you, per customer" value={`−${money(e.fee)}`} muted />
            <Row
              label={`${minutes.toLocaleString()} minutes × ${perMinute(preview!.per_minute_cents)}`}
              value={`−${money(e.minutesCost)}`}
              muted
            />
          </div>

          <div className="border-t pt-2">
            <Row
              label={profitable ? "Your profit" : "Your loss"}
              value={`${money(Math.abs(e.profit))} /mo`}
              strong
              tone={profitable ? "good" : "bad"}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {profitable
                ? `If they use every minute. ${money(e.profitIfUnused)} if they make no calls.`
                : `A customer using every minute costs you ${money(e.cost)} — price this plan above that.`}
            </p>
          </div>
        </>
      )}

      <p className="border-t pt-2 text-xs text-muted-foreground">
        Extra minutes: you charge {perMinute(derived)}
        {preview ? `, they cost you ${perMinute(preview.per_minute_cents)}` : ""} —{" "}
        {money(priceCents)} ÷ {minutes.toLocaleString()} minutes.
      </p>
    </div>
  );
}

function Row({
  label,
  value,
  muted,
  strong,
  tone,
}: {
  label: string;
  value: string;
  muted?: boolean;
  strong?: boolean;
  tone?: "good" | "bad";
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-4",
        muted && "text-muted-foreground",
      )}
    >
      <span className={strong ? "font-medium" : undefined}>{label}</span>
      <span
        className={cn(
          "shrink-0 tabular-nums",
          strong && "font-semibold",
          tone === "good" && "text-emerald-600 dark:text-emerald-400",
          tone === "bad" && "text-destructive",
        )}
      >
        {value}
      </span>
    </div>
  );
}

function PlanDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan: CustomerPlan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(plan?.name ?? "");
  const [description, setDescription] = useState(plan?.description ?? "");
  const [minutes, setMinutes] = useState(
    plan ? String(plan.included_minutes) : "",
  );
  const [price, setPrice] = useState(
    plan ? (plan.price_cents / 100).toFixed(2) : "",
  );
  const [active, setActive] = useState(plan?.is_active ?? true);

  useEffect(() => {
    if (!plan) return;
    setName(plan.name);
    setDescription(plan.description);
    setMinutes(String(plan.included_minutes));
    setPrice((plan.price_cents / 100).toFixed(2));
    setActive(plan.is_active);
  }, [plan]);

  // What Chawowa charges for a customer and for minutes — fetched, never
  // assumed, so the margin below moves when the platform rate does.
  const previewQ = useQuery({
    queryKey: ["customers", "billing-preview"],
    queryFn: customersApi.billingPreview,
  });

  const minutesNum = parseInt(minutes || "0", 10);
  const priceCents = Math.round(parseFloat(price || "0") * 100);
  // Mirrors the server's derived rate, so what you see while typing is what
  // the plan will actually charge for an extra minute.
  const derived = minutesNum > 0 ? priceCents / minutesNum : 0;

  const save = useMutation({
    mutationFn: () => {
      const body: CustomerPlanInput = {
        name,
        description,
        included_minutes: minutesNum,
        price_cents: priceCents,
        is_active: active,
      };
      return plan
        ? customersApi.updatePlan(plan.id, body)
        : customersApi.createPlan(body);
    },
    onSuccess: () => {
      toast.success(plan ? "Plan updated." : "Plan created.");
      onSaved();
      onClose();
    },
    onError: (e) => toast.error(errText(e)),
  });

  const valid = name.trim() && minutesNum > 0 && priceCents >= 0;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{plan ? "Edit plan" : "New plan"}</DialogTitle>
          <DialogDescription>
            Set the minutes and the monthly price. The rate for extra minutes
            follows from those two.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="plan-name">Plan name</Label>
            <Input
              id="plan-name"
              placeholder="Starter"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="plan-minutes">Minutes included / month</Label>
              <Input
                id="plan-minutes"
                inputMode="numeric"
                placeholder="500"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ""))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan-price">Monthly price</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  $
                </span>
                <Input
                  id="plan-price"
                  className="pl-6"
                  inputMode="decimal"
                  placeholder="99.00"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
            </div>
          </div>

          {minutesNum > 0 && priceCents > 0 ? (
            <MarginPanel
              priceCents={priceCents}
              minutes={minutesNum}
              derived={derived}
              preview={previewQ.data}
              loading={previewQ.isLoading}
            />
          ) : (
            <div className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
              Enter minutes and a price to see the extra-minute rate and what
              this plan earns you.
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="plan-desc">Description (optional)</Label>
            <Textarea
              id="plan-desc"
              rows={2}
              placeholder="Shown to your customer"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
            <div>
              <Label className="text-sm font-medium">Available</Label>
              <p className="text-xs text-muted-foreground">
                Turn off to stop offering this to new customers. Customers
                already on it keep it.
              </p>
            </div>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? "Saving…" : plan ? "Save changes" : "Create plan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
